import AppRouter from 'common/AppRouter';
import { useEffect, useState } from 'react';
import {
  PROVIDERS_MAP,
  SEARCH_DATE_FORMAT,
  SESSION_STORAGE_KEYS,
  URL_CONTEXTS
} from 'common/constants/constants';
import { SettingValues } from 'common/services/settings.service.js';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import * as BoardsService from 'common/services/boards.service';
import moment from 'moment';
import QueryString from 'query-string';
import ClubhouseValidationService from './ClubhouseValidationService';
import JiraValidationService from './JiraValidationService';
import useSettingsService from '../../hooks/useSettingsService';
import useDoraFeatureFlags from 'views/root/views/home/hooks/useDoraFeatureFlags';
import { checkIfIterationIsTooLong } from 'common/utils/utils';
import { PERIODS } from 'components/PeriodPicker/periods/constants';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';

const withPMData = WrappedComponent => props => {
  const {
    uiState,
    getIterations,
    customBoard,
    pmSensor,
    getDefaultIterations,
    history,
    routeChanged,
    location,
    DateSelectionChanged,
    isFinishedOnBoarding
  } = props;
  const { isAllControbutorsTeamSelected } = useDoraFeatureFlags();
  const { selectedTeamId, selectedPeriodKey } = uiState;
  const sessionStorageAccessor = BrowserStorageAccessor().session;

  const urlParams = new URLSearchParams(window.location.search);
  const contextFromQs = urlParams.get('context');

  const selectedPeriodFromSession = sessionStorageAccessor.get(
    SESSION_STORAGE_KEYS.SELECTED_PERIOD
  );

  const getIterationFromQs = (iterations = []) => {
    // If iteration id query param is different from uiState iteration -> change iteration according to QS iter id
    const { search } = location;

    const { iteration_id = null } = QueryString.parse(search, {
      ignoreQueryPrefix: true
    });

    const queryIteration = iterations.find(
      i => i.id === parseInt(iteration_id)
    );

    return queryIteration;
  };

  const getCustomBoardIterations = async () =>
    await getIterations({ board_id: customBoard?.id });

  const PM_SERVICES = {
    [PROVIDERS_MAP.jira.name.toLowerCase()]: JiraValidationService,
    [PROVIDERS_MAP.clubhouse.name.toLowerCase()]: ClubhouseValidationService,
    default: () => ({
      handleInvalidData: getCustomBoardIterations
    })
  };

  const pmValidationService = (
    PM_SERVICES[pmSensor?.name.toLowerCase()] || PM_SERVICES.default
  )();

  const [isLoadingIterations, setIsLoadingIterations] = useState(true);
  const [gitScopeToggle, setGitScopeToggle] = useState(null);
  const [isDefaultIterationTooLong, setIsDefaultIterationTooLong] =
    useState(false);
  const { getSettingOrDefinitionValue } = useSettingsService();

  const isPreventTooLongIterationsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isPreventTooLongIterationsEnabled
  });

  const setDefaultContainer = async (containerId, teamId) =>
    await BoardsService.setDefaultBoard(containerId, teamId);

  // Iterations context
  const getDefaultIteration = (iterations = []) => {
    const today = moment().format(SEARCH_DATE_FORMAT);

    const queryIteration = getIterationFromQs(iterations);

    if (
      iterations?.length &&
      queryIteration &&
      queryIteration.start_date !== uiState.dates.from &&
      queryIteration.end_date !== uiState.dates.to
    ) {
      return queryIteration;
    }

    // If its first day of iter, show prev, otherwise, show latest
    const orderedIterations = iterations.filter(({ isVisible }) => isVisible);

    const latestIter = orderedIterations[0];
    const prevIter = orderedIterations[1];

    const isFirstDayOfIteration = latestIter && latestIter.start_date === today;

    return isFinishedOnBoarding && isFirstDayOfIteration && prevIter
      ? prevIter
      : latestIter;
  };

  const loadIterations = async () => {
    setIsLoadingIterations(true);
    const iterations = await getDefaultIterations(
      {
        ...(selectedTeamId ? { team_id: selectedTeamId } : {})
      },
      {
        withComputed:
          history.location.pathname !== AppRouter.PAGE_DASHBOARD.path,
        freeTier: isAllControbutorsTeamSelected
      }
    );

    if (!iterations?.length) {
      const { handleInvalidData = PM_SERVICES.default().handleInvalidData } =
        pmValidationService;
      await handleInvalidData();
    }

    routeChanged({
      location,
      history,
      dateSelectionCb: iterations => {
        if (contextFromQs === URL_CONTEXTS.TEAM_ITERATION) {
          const queryIteration = getDefaultIteration(iterations);
          DateSelectionChanged(queryIteration);
          sessionStorageAccessor.set(
            SESSION_STORAGE_KEYS.SELECTED_PERIOD,
            JSON.stringify(queryIteration)
          );
        } else if (selectedPeriodKey === PERIODS.ITERATIONS.periodKey) {
          const defaultIteration = getDefaultIteration(iterations);

          if (
            checkIfIterationIsTooLong({
              iteration: defaultIteration,
              isPreventTooLongIterationsEnabled
            })
          ) {
            setIsDefaultIterationTooLong(true);
            DateSelectionChanged({
              end_date: PERIODS.PAST_30_DAYS.before.format(SEARCH_DATE_FORMAT),
              start_date: PERIODS.PAST_30_DAYS.after.format(SEARCH_DATE_FORMAT),
              selectedPeriodKey: PERIODS.PAST_30_DAYS.periodKey
            });
          } else {
            if (selectedPeriodFromSession) {
              const parsedPeriod = JSON.parse(selectedPeriodFromSession);
              DateSelectionChanged(parsedPeriod);
            } else {
              DateSelectionChanged(getDefaultIteration(iterations));
            }
          }
        }
      }
    });
    setIsLoadingIterations(false);
  };

  const debouncedLoadIterations = debounce(loadIterations, 200);

  const defaultBoardSetting = getSettingOrDefinitionValue({
    name: SettingValues.ITERATION_STRATEGY,
    teamId: selectedTeamId
  });
  const includeIterationsRegex = get(
    defaultBoardSetting,
    'includeIterationsRegex',
    null
  );
  const excludedIterationsIds = JSON.stringify(
    get(defaultBoardSetting, 'excludedIterationsIds', [])
  ); // We want compare strings and not creating new reference when the content is actually equals
  useEffect(() => {
    if (defaultBoardSetting) {
      debouncedLoadIterations();
    }
  }, [includeIterationsRegex, excludedIterationsIds, selectedTeamId]);
  return (
    <WrappedComponent
      {...props}
      setDefaultContainers={setDefaultContainer}
      isLoadingIterations={isLoadingIterations}
      isDefaultIterationTooLong={isDefaultIterationTooLong}
      setGitScopeToggle={setGitScopeToggle}
      gitScopeToggle={gitScopeToggle}
    />
  );
};

export default withPMData;
